import { memo, useCallback, useMemo } from 'react'
import {
  CrewAvatarSize,
  CrewAvatar,
} from 'components/elements/crewAvatar/crewAvatar'
import { generateImageAvatarUrl } from '@crew/utils/avatar'
import { EntityType } from '@crew/enums/domain'
import { useAppSelector } from 'states/hooks'
import { skipToken } from '@reduxjs/toolkit/query'
import classNames from 'classnames'
import { CrewCheckBox } from 'components/devextreme/crewCheckBox'
import { UserRef } from '@crew/models/refs'
import { useGetNonProjectMembersQuery } from '@crew/apis/project/projectApis'
import { GetNonProjectMembersRequest } from '@crew/apis/project/models/getNonProjectMembers/request'

export type GroupChatMemberListProps = {
  projectId: string
  onUserSelected: (userId: string, checked: boolean) => void
  selectedUserIds: string[]
}

type User = UserRef & {
  isSelected: boolean
}

// ユーザー一覧
export const GroupChatMemberList = memo((props: GroupChatMemberListProps) => {
  const keyword = useAppSelector(
    (state) => state.groupChatAddMemberDialog.searchKeyword
  )

  // キーワードに該当するユーザ一覧取得（既存メンバーは除く）
  // 三項演算子になっていて少し見づらいが、内部のパラメータがundefinedを受け付けないため三項演算子を使用している
  // undefined時はskipTokenでAPIを実行しない（キーワードテキストが空のとき）
  const getNonProjectMembersParams: GetNonProjectMembersRequest | undefined =
    props.projectId && keyword
      ? {
          projectId: props.projectId,
          displayName: keyword,
          userId: undefined,
        }
      : undefined

  const { data: getNonProjectMembersQueryResult } =
    useGetNonProjectMembersQuery(getNonProjectMembersParams ?? skipToken)

  // 選択状態を付与したユーザリストを作成
  const users: User[] = useMemo(() => {
    const searchedUsers = getNonProjectMembersQueryResult?.users ?? []

    return Object.values(searchedUsers).map((user) => ({
      ...user,
      // 選択状態を設定
      isSelected: props.selectedUserIds.includes(user.id),
    }))
  }, [getNonProjectMembersQueryResult?.users, props.selectedUserIds])

  // ユーザー選択のチェックボックス変更時
  const handleCheckboxChange = useCallback(
    (userId: string, checked: boolean) => {
      // 親コンポーネントに選択状態変更を通知
      props.onUserSelected(userId, checked)
    },
    [props]
  )

  // チェックボックスクリック時
  const handleCheckboxClick = useCallback((e: React.MouseEvent) => {
    // 親コンポーネントとチェックボックスでクリック時のイベントが重複してしまい選択状態が正しく動作しなくなるため、
    // 親コンポーネントのクリックイベントが伝播しないようにする
    e.stopPropagation()
  }, [])

  return (
    <div className="h-full flex flex-col py-2">
      {users.length > 0 &&
        Object.values(users).map((user) => {
          return (
            <div
              key={user.id} // Warning表示回避のkey指定
              className={classNames(
                'flex items-center gap-2 py-1 border-b-crew-slate-1-light dark:border-b-crew-slate-1-dark border-b-2 cursor-pointer'
              )}
              onClick={() => handleCheckboxChange(user.id, !user.isSelected)}
            >
              {/* ユーザー選択チェックボックス */}
              <div onClick={handleCheckboxClick}>
                <CrewCheckBox
                  value={user.isSelected}
                  onValueChanged={(e) =>
                    handleCheckboxChange(user.id, e.value ?? false)
                  }
                />
              </div>
              {/* ユーザーアバター */}
              <CrewAvatar
                displayName={user.displayName}
                imageURL={generateImageAvatarUrl(EntityType.User, user.id)}
                size={CrewAvatarSize.xs}
                cacheValue={user.id + user.version}
              />
              {/* ユーザー名 */}
              <span className="text-crew-blue-3-light dark:text-crew-blue-2-dark max-w-xl truncate whitespace-nowrap overflow-hidden">
                {user.displayName}
              </span>
            </div>
          )
        })}
    </div>
  )
})
